// GovernanceDualAuth — dual-auth approval queue (GOV-C, PAM Epic 11, Task 8).
// Lists pending governance actions proposed by other admins; second approver
// can approve.
//
// OIDCPAM Gate-6 Phase 6a: migrated off MUI onto
// @devnw/cyber-iam/rbac/DualAuthApprovalQueue — the exact parity component
// built from this page. The component owns self-approval blocking (via the
// REQUIRED `currentUserId` prop), the irreversible-reject two-phase confirm,
// and per-row mutual exclusion between approve/reject; this page still owns
// both API calls, the wire-mapping (../api/governanceDualAuthMapping), tenant
// gating, and wiring onApprove/onReject to the right mutations.
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import DualAuthApprovalQueue from '@devnw/cyber-iam/rbac/DualAuthApprovalQueue/DualAuthApprovalQueue'
import type {
  GovernanceAction,
} from '@devnw/cyber-iam/iam/types'
import { useAdminGenClient } from '../api/genClient'
import { useActiveTenant } from '../layout/TenantSwitcher'
import { useAuth } from '../auth/AuthContext'
import {
  toIamGovernanceAction,
  type WireGovernanceAction,
} from '../api/governanceDualAuthMapping'

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Request failed'
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('lg')};
`

const MutedText = styled.p`
  margin: 0;
  color: ${({ theme }) => asUnified(theme).text.secondary};
`

export const GovernanceDualAuth = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const { claims } = useAuth()
  const client = useAdminGenClient()
  const currentUserId = claims?.sub ?? ''

  const [actions, setActions] = useState<GovernanceAction[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (): Promise<void> => {
    if (!tenant) return
    setLoading(true)
    setError(null)
    try {
      const data = await client.adminGovernanceListActions({ tenantId: tenant })
      // The generated client's fromWire() camelCases the opaque
      // Array<Record<string, unknown>> response — see
      // ../api/governanceDualAuthMapping.ts for the real (camelCase) shape.
      setActions(
        ((data.actions ?? []) as unknown as WireGovernanceAction[]).map(
          toIamGovernanceAction
        )
      )
    } catch (e: unknown) {
      setError(errMessage(e))
    } finally {
      setLoading(false)
    }
  }, [tenant, client])

  useEffect(() => {
    load()
      .then(() => {})
      .catch(() => {})
  }, [load])

  const handleRefresh = useCallback((): void => {
    load()
      .then(() => {})
      .catch(() => {})
  }, [load])

  const handleApprove = async (action: GovernanceAction): Promise<void> => {
    if (!tenant) return
    await client.adminGovernanceApprove({ tenantId: tenant, actionId: action.id })
  }

  const handleReject = async (action: GovernanceAction): Promise<void> => {
    if (!tenant) return
    await client.adminGovernanceReject({ tenantId: tenant, actionId: action.id })
  }

  if (!tenant) {
    return (
      <PageContainer>
        <MutedText>
          Select a tenant to view the dual-auth approval queue.
        </MutedText>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <DualAuthApprovalQueue
        testID="governance-dual-auth"
        title="Dual-Auth Approval Queue"
        description="Sensitive governance actions require approval from a second distinct admin. You cannot approve your own proposed action — the server enforces this regardless of UI state."
        actions={actions}
        currentUserId={currentUserId}
        onApprove={handleApprove}
        onReject={handleReject}
        onRefresh={handleRefresh}
        loading={loading}
        error={error ?? undefined}
        emptyText="No governance actions in the queue."
      />
    </PageContainer>
  )
}
